// useReservationsManager.ts
// Custom hook to manage restaurant reservations and their impact on mesa states

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getTodayReservationsByLocalIdAndDate } from '../../../../../crud/clients_api/client_reservations';
import { ReservationDetails } from '../types';
import { Salon } from '../../../Configuracion/types';

interface UseReservationsManagerProps {
  hosterMode: boolean;
  salones: Salon[];
  setSalones: (salones: Salon[]) => void;
}

interface UseReservationsManagerReturn {
  reservationsToday: ReservationDetails[];
  setReservationsToday: (reservations: ReservationDetails[]) => void;
  filteredReservationsInAHour: ReservationDetails[];
  setFilteredReservationsInAHour: (reservations: ReservationDetails[]) => void;
  fetchReservations: () => void;
}

// Filter reservations for today only
const filterReservationOnlyToday = (reservations: ReservationDetails[]): ReservationDetails[] => {
  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
  return reservations.filter(reservation => {
    // Extract only the date part (YYYY-MM-DD) in case it comes with time
    const datePart = reservation.reservation_date.split('T')[0];
    return datePart === today && reservation.attended !== true;
  });
};

// Filter reservations for today within the next hour and 20 minutes
const filterReservationsTodayInNextHour = (reservations: ReservationDetails[]): ReservationDetails[] => {
  const today = new Date().toLocaleDateString('en-CA');
  const now = new Date();
  const currentSlot = now.getHours() * 4 + Math.floor(now.getMinutes() / 15);
  // Calculate number of slots for 1 hour and 20 minutes (80 minutes)
  const slotsIn80Minutes = Math.floor(80 / 15);
  return reservations.filter(reservation => {
    const datePart = reservation.reservation_date.split('T')[0];
    return (
      datePart === today &&
      currentSlot <= reservation.init && reservation.init <= currentSlot + slotsIn80Minutes
    );
  });
};

export const useReservationsManager = ({
  hosterMode,
  salones,
  setSalones,
}: UseReservationsManagerProps): UseReservationsManagerReturn => {
  const [reservationsToday, setReservationsToday] = useState<ReservationDetails[]>([]);
  const [filteredReservationsInAHour, setFilteredReservationsInAHour] = useState<ReservationDetails[]>([]);

  // Fetch reservations (memoized for passing to child components)
  const fetchReservations = useCallback(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user?.local_id ?? 0;
    const today = new Date().toLocaleDateString('en-CA');
    
    getTodayReservationsByLocalIdAndDate(localId, today)
      .then((response) => {
        setReservationsToday(filterReservationOnlyToday(response.data));
      })
      .catch(() => {
        toast.error('Error obteniendo reservas del día');
      });
  }, []);

  // Initial fetch when hosterMode is enabled
  useEffect(() => {
    if (!hosterMode) return;
    
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user?.local_id ?? 0;
    const today = new Date().toLocaleDateString('en-CA');
    
    getTodayReservationsByLocalIdAndDate(localId, today)
      .then((response) => {
        setReservationsToday(filterReservationOnlyToday(response.data));
      })
      .catch(() => {
        toast.error('Error obteniendo reservas del día');
      });
  }, [hosterMode]);

  // Update filtered reservations when reservationsToday changes
  useEffect(() => {
    setFilteredReservationsInAHour(filterReservationsTodayInNextHour(reservationsToday));
  }, [reservationsToday]);

  // Update mesa states based on upcoming reservations
  useEffect(() => {
    if (!salones.length) return;
    
    // Get set of mesa IDs that should be blocked (confirmed reservations that haven't arrived yet)
    const reservedSet = new Set(
      filteredReservationsInAHour
        .filter(r => r.approved === true && r.confirmed === true && r.cancelled === false && r.arrived === false && r.attended === false)
        .map(r => Number(r.mesa_id))
    );
    
    // Get set of ALL mesa IDs from ALL reservations in reservationsToday (including cancelled ones)
    // This helps us identify mesas that had reservations and may need their state updated
    const allReservationMesaIds = new Set(
      reservationsToday.map(r => Number(r.mesa_id))
    );
    
    let changed = false;
    const updatedSalones = salones.map(salon => {
      let salonChanged = false;
      const mesas = salon.mesas.map(mesa => {
        const mesaId = Number(mesa.id);
        
        // If this mesa has a confirmed reservation that hasn't arrived, block it
        if (reservedSet.has(mesaId) && mesa.state !== false) {
          salonChanged = true;
          return { ...mesa, state: false };
        }
        
        // If this mesa had a reservation but it's now cancelled/attended/arrived, unblock it
        // Only unblock if: 
        // 1. Mesa is currently blocked (state === false)
        // 2. Mesa is not in the reserved set (no active confirmed reservation)
        // 3. Mesa is not being used (not used)
        if (allReservationMesaIds.has(mesaId) && !reservedSet.has(mesaId) && mesa.state === false && !mesa.used) {
          salonChanged = true;
          return { ...mesa, state: true };
        }
        
        return mesa;
      });
      if (salonChanged) {
        changed = true;
        return { ...salon, mesas };
      }
      return salon;
    });
    
    if (changed) setSalones(updatedSalones);
  }, [filteredReservationsInAHour, reservationsToday, salones, setSalones]);

  return {
    reservationsToday,
    setReservationsToday,
    filteredReservationsInAHour,
    setFilteredReservationsInAHour,
    fetchReservations,
  };
};
